import React from "react"

import { Check, ChevronsUpDown, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { Person } from "@/types/person"
import { formatPersonShortName } from "@/utils/personDisplay"

interface PersonComboboxProps {
  value?: string // person ID
  onValueChange: (personId: string, person: Person | undefined) => void
  disabled?: boolean
  placeholder?: string
  className?: string
  // Filters to match shift requirements
  area?: string // Sheriff area filter
  location?: string // Location filter
  sheriffType?: string // Type filter (e.g., "HivedVan")
  employer?: string // Employer filter (e.g., "Hived")
  // People data passed from parent
  people?: Person[]
  isLoading?: boolean
}

export const PersonCombobox: React.FC<PersonComboboxProps> = ({
  value,
  onValueChange,
  disabled = false,
  placeholder = "Select a person...",
  className,
  area,
  location,
  sheriffType,
  employer,
  people: peopleProp = [],
  isLoading: isLoadingProp = false,
}) => {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState("")

  // Filter people based on search query and filters (client-side)
  const filteredPeople = React.useMemo(() => {
    let filtered = peopleProp

    // Apply filters only if they are provided
    // Note: Filters are optional hints, not strict requirements
    // This allows showing all people if no specific filters match
    if (area && area !== "all") {
      const areaFiltered = filtered.filter((p) =>
        p.sheriff_areas?.includes(area as (typeof p.sheriff_areas)[number]),
      )
      // Only apply filter if it doesn't eliminate everyone
      if (areaFiltered.length > 0) {
        filtered = areaFiltered
      }
    }
    if (location && location !== "all") {
      const locationFiltered = filtered.filter((p) => p.location?.includes(location))
      if (locationFiltered.length > 0) {
        filtered = locationFiltered
      }
    }
    if (sheriffType && sheriffType !== "all") {
      const typeFiltered = filtered.filter((p) => p.sheriff_type === sheriffType)
      if (typeFiltered.length > 0) {
        filtered = typeFiltered
      }
    }
    if (employer && employer !== "all") {
      const employerFiltered = filtered.filter((p) => p.sheriff_employer === employer)
      if (employerFiltered.length > 0) {
        filtered = employerFiltered
      }
    }

    filtered = filtered.filter((p) => p.person_id !== null)

    // Apply search query (this is always strict)
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (p) =>
          p.first_name?.toLowerCase().includes(query) ||
          p.surname?.toLowerCase().includes(query) ||
          p.email?.toLowerCase().includes(query) ||
          p.person_id?.toLowerCase().includes(query) ||
          p.person?.toLowerCase().includes(query),
      )
    }

    return filtered
  }, [peopleProp, area, location, sheriffType, employer, searchQuery])

  // Find selected person
  const selectedPerson = React.useMemo(() => {
    return peopleProp.find((p) => p.person === value)
  }, [peopleProp, value])

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", !value && "text-muted-foreground", className)}
          disabled={disabled || isLoadingProp}
        >
          {isLoadingProp ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading people...
            </>
          ) : selectedPerson ? (
            <>{formatPersonShortName(selectedPerson)}</>
          ) : (
            placeholder
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name, email, or ID..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>{isLoadingProp ? "Loading..." : "No person found."}</CommandEmpty>
            <CommandGroup>
              {filteredPeople.map((person) => (
                <CommandItem
                  key={person.person}
                  value={person.person}
                  onSelect={(currentValue) => {
                    const selectedPerson = filteredPeople.find((p) => p.person === currentValue)
                    onValueChange(currentValue === value ? "" : currentValue, selectedPerson)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === person.person ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <div className="flex flex-col">
                    <span>
                      {person.first_name} {person.surname}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {person.person_id} • {person.email || "No email"}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
